import React, {
  useEffect,
  useState,
  lazy,
  Suspense,
} from 'react';
import { IconAsChunk } from '@boschebike/ebds-react-icons';
import { setLangChangeRequest } from './sendLangChangeRequest.js';
import { useSearchSuggestionProvider } from './hooks/useSearchSuggestionProvider.js';
import { useNavigationLinks } from './helpers/links.js';

const Header = lazy(() => import('@boschebike/ebds-react/dist-esm/components/Header.js'));
const HeaderMenu = lazy(() => import('@boschebike/ebds-react/dist-esm/components/HeaderMenu.js'));
/**
 * @description Replace the url sub directory with query if it exists
 */
function appendUrlSubDirectoryWithQuery(
  href,
  query,
  subDirectory,
) {
  if (href) {
    const url = new URL(href);

    const currentSearchParams = new URLSearchParams(url.searchParams);
    const currentQuery = new URLSearchParams();

    if (currentSearchParams.has(query)) {
      currentQuery.set(query, currentSearchParams.get(query));

      return `${subDirectory}?${currentQuery.toString()}`;
    }
  }

  return subDirectory;
}

/**
 * @typedef {import('../App.d.ts').HeaderQuickLink} HeaderQuickLink
 */
/**
 * @typedef {import('../App.d.ts').QuickLink} QuickLink
 */
/**
 * @param {HeaderQuickLink[]} quickLinks
 * @param {string} currentLocation
 * @returns {QuickLink[]}
 */
const createUserClickLinks = (quickLinks, currentLocation) => {
  return quickLinks.map((x) => {
    const menuItem = {
      text: x.text,
      url: x?.iconName === 'login'
        ? appendUrlSubDirectoryWithQuery(currentLocation, 'returnTo', x.url)
        : x.url,
      isExternal: x.isExternal,
      icon: x.iconName ? <IconAsChunk iconName={x.iconName} /> : undefined,
    };

    if (x.subMenu) {
      menuItem.subMenu = createUserClickLinks(x.subMenu, currentLocation);
    }

    return menuItem;
  });
};

/**
 * @typedef {import('../App.d.ts').NoSSRHeaderProps} NoSSRHeaderProps
 */
/**
 * @param {NoSSRHeaderProps} props
 */
export const BrowserHeader = ({
  navigation,
  quickLinks,
  userData,
  withSearch,
  meta,
}) => {
  const languages = userData.lang.possible.map((el) => ({ code: el.code, label: el.name }));

  const navigationLinks = navigation.links;
  const [currentLocation, setCurrentLocation] = useState(null);
  const links = useNavigationLinks(navigationLinks, currentLocation);

  const searchSuggestionsProvider = useSearchSuggestionProvider(withSearch);

  useEffect(() => {
    if (typeof location !== 'undefined') {
      setCurrentLocation(location.href);
    }
  }, [currentLocation]);

  return (
    <Suspense fallback={null}>
      <Header
        lang={userData.lang.current}
        onLogoClick={() => {
          location.href = '/';
        }}
        quickLinks={createUserClickLinks(quickLinks, currentLocation)}
        menu={(
          <Suspense fallback={null}>
            <HeaderMenu links={links} />
          </Suspense>
        )}
        currentLang={userData.lang.current}
        languages={languages.length > 1 ? languages : undefined}
        onLanguageChange={setLangChangeRequest}
        searchSuggestionsProvider={searchSuggestionsProvider}
        withSearch={withSearch}
        withSearchOpenKeyboardShortcut
        searchInputPlaceholder={meta.searchInputPlaceholder}
        onSearchSubmit={(event) => {
          location.href = `/search?q=${encodeURIComponent(event.detail)}`;
        }}
        subBrand={meta.subBrand}
      />
    </Suspense>

  );
};

export default Header;
